import React from 'react';
import * as i18n from 'i18next';
import { reduxForm } from 'redux-form';
import { Button, Col, Container } from 'reactstrap';
import {
  FORMS,
  LANGUAGE_CODE_KEY,
  RELATIVE_PAGE_PATHS,
} from '../../utils/constants';
import { formUtils } from '../../utils/formUtils';
import { connect } from 'react-redux';
import { Fields } from '../common/Fields';
import navigationMethods from '../../utils/navigationUtils';
import {
  addSpecialist,
  getSpecialist,
  getSpecialists,
  updateSpecialist,
  deleteSpecialist,
  showDeleteModal,
  closeModal,
  showInfoModal,
  showRedConfirmModal,
  showErrorModal,
  showSuccessModal,
  shareJournalThroughEmail,
  showConfirmModal,
  handleStopSharingJournalClick,
  handleShareJournalClick,
  setJournalTokenActiveStatusToFalse,
  setJournalTokenActiveStatusToTrue,
} from '../../actions';
import { getAxiosWithToken } from '../../utils/webApi';

class SpecialistDetailsScreen extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isJournalShared: null,
      emailMessageShown: false,
      loading: true,
      hasSharedAnalyticsLink: false,
      sharedAnalyticsLinkUrl: '',
      isSharingAnalytics: false,
      isCopyingAnalyticsLink: false,
      isStoppingAnalyticsShare: false,
    };
  }

  async componentDidMount() {
    const id = this.props.match.params.id;
    if (id) {
      const languageCode = localStorage.getItem(LANGUAGE_CODE_KEY);
      if (!this.props.specialists.length) {
        await this.props.getSpecialists();
      }
      await this.props.getSpecialist(id, languageCode);
      this.setState({
        isJournalShared: this.props.currentSpecialist.isJournalShared,
        loading: false,
      });
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentSpecialist !== prevProps.currentSpecialist) {
      this.setState({
        isJournalShared: this.props.currentSpecialist.isJournalShared,
      });
    }
  }

  handleSubmit = (formValues) => {
    const id = this.props.match.params.id;
    const languageCode = localStorage.getItem(LANGUAGE_CODE_KEY);
    if (id) {
      this.props.updateSpecialist(id, formValues, languageCode);
    } else {
      this.props.addSpecialist(formValues, languageCode);
    }
  };

  onDelete = () => {
    const id = this.props.match.params.id;
    const message = i18n.t('specialistsScreen:confirmDelete');
    this.props.showDeleteModal({
      message,
      onDelete: () => {
        this.props.deleteSpecialist(id);
        this.props.closeModal();
      },
    });
  };

  normalizeEncryptedUserId = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  buildAnalyticsShareUrl = (encryptedUserId, token) => {
    const normalizedEncryptedUserId =
      this.normalizeEncryptedUserId(encryptedUserId);

    return (
      `${window.location.origin}` +
      `${RELATIVE_PAGE_PATHS.ANALYTICS_READ_ONLY}` +
      `?user-id=${encodeURIComponent(normalizedEncryptedUserId)}` +
      `&token=${encodeURIComponent(token)}` +
      `&type=${encodeURIComponent('growth')}`
    );
  };

  handleShareAnalytics = async () => {
    try {
      this.setState({ isSharingAnalytics: true });
      const response = await getAxiosWithToken().post(
        'trackers/shared-analytics-link'
      );

      const returnedData = response.data;
      if (!returnedData?.success) {
        this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
        return;
      }

      const encryptedUserId = returnedData.data?.encryptedUserId;
      const token = returnedData.data?.token;

      if (!encryptedUserId || !token) {
        this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
        return;
      }

      const shareUrl = this.buildAnalyticsShareUrl(encryptedUserId, token);
      this.setState({
        hasSharedAnalyticsLink: true,
        sharedAnalyticsLinkUrl: shareUrl,
      });

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt(i18n.t('analytics:shared.copyThisLink'), shareUrl);
        return;
      }

      this.props.showSuccessModal(i18n.t('analytics:shared.shareCopied'));
    } catch (error) {
      console.error('Error generating share analytics link:', error);
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
    } finally {
      this.setState({ isSharingAnalytics: false });
    }
  };

  handleCopySharedAnalyticsLink = async () => {
    const { sharedAnalyticsLinkUrl } = this.state;

    if (!sharedAnalyticsLinkUrl) {
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
      return;
    }

    try {
      this.setState({ isCopyingAnalyticsLink: true });

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(sharedAnalyticsLinkUrl);
      } else {
        window.prompt(
          i18n.t('analytics:shared.copyThisLink'),
          sharedAnalyticsLinkUrl
        );
        return;
      }

      this.props.showSuccessModal(i18n.t('analytics:shared.shareCopied'));
    } catch (error) {
      console.error('Error copying share analytics link:', error);
      this.props.showErrorModal(i18n.t('analytics:shared.shareFailed'));
    } finally {
      this.setState({ isCopyingAnalyticsLink: false });
    }
  };

  handleStopSharingAnalytics = async () => {
    try {
      this.setState({ isStoppingAnalyticsShare: true });
      const response = await getAxiosWithToken().patch(
        'trackers/shared-analytics-link/disable'
      );

      const returnedData = response.data;
      if (!returnedData?.success) {
        this.props.showErrorModal(
          'Unable to disable shared analytics links. Please try again.'
        );
        return;
      }

      const disabledCount =
        returnedData?.data?.disabledLinksCount ??
        returnedData?.data?.DisabledLinksCount ??
        0;

      if (!disabledCount) {
        this.props.showErrorModal(
          'No active shared analytics links were found to disable.'
        );
        return;
      }

      this.props.showSuccessModal(
        'Shared analytics links are now disabled and no longer accessible.'
      );
      this.setState({
        hasSharedAnalyticsLink: false,
        sharedAnalyticsLinkUrl: '',
      });
    } catch (error) {
      console.error('Error disabling shared analytics links:', error);
      this.props.showErrorModal(
        'Unable to disable shared analytics links. Please try again.'
      );
    } finally {
      this.setState({ isStoppingAnalyticsShare: false });
    }
  };

  renderDeleteButton = () => {
    if (this.props.match.params.id) {
      return (
        <Button
          onClick={this.onDelete}
          className="greenBtn header next uppercase secondary danger mt-3"
        >
          {i18n.t('buttons:delete')}
        </Button>
      );
    }
    return null;
  };

  renderShareButton = () => {
    const { currentSpecialist } = this.props;
    const id = this.props.match.params.id;
    const isJournalShared = this.state.isJournalShared;
    const shareButton = i18n.t('specialistsScreen:buttons:shareJournal');
    const stopSharingButton = i18n.t(
      'specialistsScreen:buttons:stopsharingJournal'
    );

    if (id) {
      if (!currentSpecialist.email && !this.state.emailMessageShown) {
        this.props.showInfoModal(i18n.t('contactsScreen:addEmail'));
        this.setState({ emailMessageShown: true });
        return null;
      }

      const buttonText = isJournalShared ? stopSharingButton : shareButton;
      const buttonClass = isJournalShared
        ? 'redBtn header next uppercase mt-3'
        : 'greenBtn header next uppercase mt-3';

      if (id && currentSpecialist.email) {
        return (
          <Button
            onClick={() => {
              const message = isJournalShared
                ? i18n.t('contactsScreen:confirmStopSharingJournal')
                : i18n.t('contactsScreen:confirmShareJournal');
              if (!isJournalShared) {
                this.props.showConfirmModal({
                  message,
                  onConfirm: () => {
                    this.props.shareJournalThroughEmail(currentSpecialist.email);
                    this.props.handleShareJournalClick(this.props.match.params.id);
                    this.setState({ isJournalShared: true });
                  },
                });
              } else {
                this.props.showRedConfirmModal({
                  message,
                  onConfirm: () => {
                    this.props.handleStopSharingJournalClick(
                      this.props.match.params.id
                    );
                    this.props.setJournalTokenActiveStatusToFalse(
                      this.props.currentSpecialist.email
                    );
                    this.setState({ isJournalShared: false });
                  },
                });
              }
            }}
            className={buttonClass}
          >
            {buttonText}
          </Button>
        );
      }
      return null;
    }
    return null;
  };

  renderShareAnalyticsButtons = () => {
    const { currentSpecialist } = this.props;
    const id = this.props.match.params.id;
    const {
      hasSharedAnalyticsLink,
      isSharingAnalytics,
      isCopyingAnalyticsLink,
      isStoppingAnalyticsShare,
    } = this.state;

    if (!id || !currentSpecialist.email) {
      return null;
    }

    const isDisabled =
      isSharingAnalytics || isCopyingAnalyticsLink || isStoppingAnalyticsShare;

    if (!hasSharedAnalyticsLink) {
      return (
        <Button
          onClick={this.handleShareAnalytics}
          className="greenBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.share')}
        </Button>
      );
    }

    return (
      <>
        <Button
          onClick={this.handleCopySharedAnalyticsLink}
          className="greenBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.copyThisLink')}
        </Button>
        <Button
          onClick={this.handleStopSharingAnalytics}
          className="redBtn header next uppercase mt-3"
          disabled={isDisabled}
        >
          {i18n.t('analytics:shared.stopSharing')}
        </Button>
      </>
    );
  };

  render() {
    const id = parseInt(this.props.match.params.id);

    const specialistNumber =
      this.props.specialists.findIndex((specialist) => specialist.id === id) + 1;

    const isNewSpecialist =
      navigationMethods.getRoute() === RELATIVE_PAGE_PATHS.ADD_SPECIALISTS;
    const title = isNewSpecialist
      ? i18n.t('specialistsScreen:addSpecialist')
      : i18n.t('specialistsScreen:specialist', {
        word1: specialistNumber,
      });
    const buttonName = isNewSpecialist
      ? i18n.t('specialistsScreen:buttons.add')
      : i18n.t('buttons:saveChanges');

    if (this.state.loading) {
      return <div></div>;
    }

    return (
      <Container className="noPadding">
        <Col xs={12} sm={12} md={4} lg={4} className="centred noPadding">
          <form
            onSubmit={this.props.handleSubmit(this.handleSubmit)}
            className="flexContainerColumn flexContainerCentered profile-form fix-padding"
          >
            <div className="flexContainerCentered">
              <p className="header mainGreenColor header-small uppercase">
                {title}
              </p>
            </div>

            <div className="flexContainerColumn flexContainerCentered mt-3">
              {Fields.specialists.name()}
              {Fields.specialists.specialty()}
              <div className="mt-4">
                {Fields.commonFields.email()}
                {Fields.specialists.phone()}
                {Fields.specialists.address({
                  className: 'specialists-address input-container',
                  rows: 10,
                })}
                {Fields.specialists.isFamilyPhysician()}
              </div>
            </div>

            <Col
              xs={10}
              sm={10}
              md={10}
              lg={8}
              className="flexContainerColumn centred mt-5 mb-5"
            >
              <Button
                onClick={this.props.handleSubmit(this.handleSubmit)}
                className="greenBtn header next uppercase"
              >
                {buttonName}
              </Button>

              {/* Share Journal */}
              {this.renderShareButton()}

              {/* Share Analytics - directly under Share Journal */}
              {this.renderShareAnalyticsButtons()}

              {this.renderDeleteButton()}
            </Col>
          </form>
        </Col>
      </Container>
    );
  }
}

const specialistForm = reduxForm({
  form: FORMS.SPECIALIST_FORM,
  validate: formUtils.validateSpecialists,
  destroyOnUnmount: true,
  enableReinitialize: true,
})(SpecialistDetailsScreen);

const mapStateToProps = (state, props) => {
  const id = props.match.params.id;
  return {
    initialValues: id ? state.specialists.specialist : {},
    specialists: state.specialists.specialists,
    currentSpecialist: id ? state.specialists.specialist : {},
  };
};

export default connect(mapStateToProps, {
  setJournalTokenActiveStatusToTrue,
  setJournalTokenActiveStatusToFalse,
  handleStopSharingJournalClick,
  handleShareJournalClick,
  addSpecialist,
  getSpecialist,
  getSpecialists,
  updateSpecialist,
  deleteSpecialist,
  showDeleteModal,
  closeModal,
  showInfoModal,
  showErrorModal,
  showSuccessModal,
  shareJournalThroughEmail,
  showConfirmModal,
  showRedConfirmModal,
})(specialistForm);
